import { useState } from 'react'

import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { EntityImage } from '../../common/components/EntityImage.js'
import { T } from '../../i18n/components/T.js'
import { useStaticIntl } from '../../i18n/hooks/useStaticIntl.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { MediaPicker } from '../../MediaPicker/MediaPicker.js'
import {
  convertIdToGlyph,
  checkIconById,
} from '../../MediaPicker/utils/utils.js'
import { GenericSettings } from '../../Views/components/GenericSettings.js'
import { type ReactionItem, ReactionStates } from '../types.js'
import { backfillActiveState } from '../utils/converters.js'
import { reactionStatesFactory } from '../utils/selectOptions.js'
import { slugify } from '../utils/utils.js'

import { ColorSettings } from './ColorSettings.js'

interface Props {
  reaction: ReactionItem
  isNew: boolean
  checkIfReactionNew: (id: string) => boolean
  setReaction: (reaction: ReactionItem) => void
  goBack?: () => void
}
export const ReactionSettings = ({
  reaction,
  isNew,
  checkIfReactionNew,
  setReaction,
  goBack,
}: Props) => {
  const { $t } = useI18n()
  const reactionStates = useStaticIntl(reactionStatesFactory)
  const [nameError, setNameError] = useState(false)
  const [keyError, setKeyError] = useState(false)
  const { key, name, glyphId, color, activeName, activeGlyphId, activeColor } =
    backfillActiveState(reaction)
  const [reactionName, setReactionName] = useState<string>(name)
  const [reactionKey, setReactionKey] = useState<string>(key)
  const [reactionState, setReactionState] = useState<ReactionStates>(
    ReactionStates.default,
  )
  const icon = convertIdToGlyph(glyphId)
  const isIcon = checkIconById(glyphId)
  const activeIcon = convertIdToGlyph(activeGlyphId)
  const isActiveIcon = checkIconById(activeGlyphId)

  return (
    <GenericSettings
      title={name}
      description={
        <T
          defaultMessage="Customize the look of reactions in both their default and active states."
          id="Admin.CMS.Reactions.Description"
        />
      }
      goBack={goBack}
    >
      <div className="space-y-5">
        <FormControl.ButtonGroupSelect
          name="reactionState"
          fullWidth
          value={reactionState}
          onChange={(reactionState: ReactionStates) => {
            setReactionState(reactionState)
          }}
          items={reactionStates}
        />
        {reactionState === ReactionStates.default && (
          <div className="space-y-5">
            <FormControl.Input
              name="reactionName"
              label={<T defaultMessage="Label" id="Generics.Label" />}
              placeholder={$t({
                defaultMessage: 'Label',
                id: 'Generics.Label',
              })}
              helperText={$t({
                defaultMessage:
                  'The label of the reaction when it is in its default state.',
                id: 'Admin.CMS.Reactions.DefaultLabel.HelperText',
              })}
              value={reactionName}
              invalid={nameError}
              onChange={e => {
                const { value } = e.target
                setReactionName(value)

                if (!value || !value.trim()) {
                  setNameError(true)
                } else {
                  setNameError(false)
                  if (!isNew) {
                    setReaction({
                      ...reaction,
                      name: value,
                    })
                    return
                  }

                  setReaction({
                    ...reaction,
                    name: value,
                    key: slugify(value),
                  })
                  setReactionKey(slugify(value))
                }
              }}
              error={$t({
                defaultMessage:
                  'Please provide a unique name for this reaction',
                id: 'Admin.CMS.Reactions.ReactionName.ErrorMessage',
              })}
            />
            <FormControl.Input
              name="reactionKey"
              label={<T defaultMessage="Key" id="Generics.Key" />}
              placeholder={$t({
                defaultMessage: 'Key',
                id: 'Generics.Key',
              })}
              helperText={$t({
                defaultMessage:
                  'A unique identifier for tracking reactions and their analytics. This cannot be changed later.',
                id: 'Admin.CMS.Reactions.Key.HelperText',
              })}
              value={reactionKey}
              invalid={keyError}
              onChange={e => {
                const { value } = e.target
                setReactionKey(slugify(value))

                if (!value || !value.trim()) {
                  setKeyError(true)
                } else {
                  setKeyError(false)
                  if (checkIfReactionNew(value)) {
                    setReaction({ ...reaction, key: slugify(value) })
                  } else {
                    setKeyError(true)
                  }
                }
              }}
              error={$t({
                defaultMessage: 'Please provide a key for this reaction',
                id: 'Admin.CMS.Reactions.ReactionKey.ErrorMessage',
              })}
              disabled={!isNew}
            />
            <MediaPicker
              value={icon}
              onChange={icon => {
                const glyphId = icon?.id ?? ''
                const isIcon = checkIconById(glyphId)
                const newColor = isIcon ? reaction.color : null

                setReaction({
                  ...reaction,
                  glyphId,
                  color: newColor,
                  itemIcon: glyphId ? (
                    <EntityImage
                      entity={{
                        image: convertIdToGlyph(glyphId),
                        name: name ?? '',
                      }}
                      size="lg"
                      displayColor={color}
                    />
                  ) : (
                    <SvgIcon name="emotion" size="lg" />
                  ),
                })
              }}
              actionTitle={$t({
                defaultMessage: 'Click to select an icon or emoji',
                id: 'Admin.CMS.Reactions.ClickToSelectIcon',
              })}
              displayColor={color}
              iconStyle="line"
              hasEmoji
              hasIcon
              hasUpload={false}
            />
            {isIcon && (
              <ColorSettings
                color={color}
                setColor={newColor =>
                  setReaction({
                    ...reaction,
                    color: newColor,
                    itemIcon: glyphId ? (
                      <EntityImage
                        entity={{
                          image: convertIdToGlyph(glyphId),
                          name: name ?? '',
                        }}
                        size="lg"
                        displayColor={newColor}
                      />
                    ) : (
                      <SvgIcon name="emotion" size="lg" />
                    ),
                  })
                }
              />
            )}
          </div>
        )}
        {reactionState === ReactionStates.active && (
          <div className="space-y-5">
            <FormControl.Input
              name="activeName"
              label={
                <T defaultMessage="Active label" id="Generics.ActiveLabel" />
              }
              placeholder={$t({
                defaultMessage: 'Active label',
                id: 'Generics.ActiveLabel',
              })}
              helperText={$t({
                defaultMessage:
                  'The label of the reaction when it is in its active state. If not provided, the default label will be used.',
                id: 'Admin.CMS.Reactions.ActiveLabel.HelperText',
              })}
              value={activeName}
              onChange={e =>
                setReaction({ ...reaction, activeName: e.target.value })
              }
            />
            <MediaPicker
              value={activeIcon}
              onChange={icon => {
                const activeGlyphId = icon?.id ?? ''
                const isIcon = checkIconById(activeGlyphId)
                const newActiveColor = isIcon ? reaction.activeColor : null

                setReaction({
                  ...reaction,
                  activeGlyphId,
                  activeColor: newActiveColor,
                })
              }}
              displayColor={activeColor}
              iconStyle="solid"
              hasEmoji
              hasIcon
              hasUpload={false}
              actionTitle={$t({
                defaultMessage: 'Click to select an icon or emoji',
                id: 'Admin.CMS.Reactions.ClickToSelectIcon',
              })}
              helperText={$t({
                defaultMessage:
                  'Represents the reaction when in its active state. If not provided, the default icon will be used.',
                id: 'Admin.CMS.Reactions.ActiveIcon.HelperText',
              })}
            />
            {isActiveIcon && (
              <ColorSettings
                color={activeColor}
                setColor={newColor =>
                  setReaction({ ...reaction, activeColor: newColor })
                }
              />
            )}
          </div>
        )}
      </div>
    </GenericSettings>
  )
}
